import clsx from 'clsx';
import { useState } from 'react';
import { BiX } from 'react-icons/bi';

import { ButtonPill } from '@/components/Button';
import { useClickOutside } from '@/hooks/useClickOutside';

import s from './Header.module.scss';

const clearEvent = { target: { value: '' } } as React.ChangeEvent<HTMLInputElement>;

export const HeaderInput = ({
  icon,
  onChange,
  value,
  className,
  placeholder,
}: {
  icon: React.ReactElement;
  value: string;
  onChange(v: React.ChangeEvent<HTMLInputElement>): void;
  className?: string;
  placeholder: string;
}) => {
  return (
    <label className={clsx(s.HeaderInput, className)}>
      {icon && <div className={s.icon}>{icon}</div>}
      <input type="text" placeholder={placeholder} value={value} onChange={(e) => onChange(e)} />
      {!!value && (
        <button
          type="button"
          className={s.clear}
          aria-label="Clear search"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onChange(clearEvent)}
        >
          <BiX />
        </button>
      )}
    </label>
  );
};

export const DesktopHeaderInput = ({
  icon,
  onChange,
  value,
  className,
  placeholder,
}: {
  icon: React.ReactElement;
  value: string;
  onChange(v: React.ChangeEvent<HTMLInputElement>): void;
  className?: string;
  placeholder: string;
}) => {
  return (
    <label className={clsx(s.DesktopHeaderInput, className)}>
      {icon && <div className={s.icon}>{icon}</div>}
      <div className={s.body}>
        <input
          className={s.text}
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e)}
        />
        {!!value && (
          <button
            type="button"
            className={s.clear}
            aria-label="Clear search"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onChange(clearEvent)}
          >
            <BiX />
          </button>
        )}
      </div>
    </label>
  );
};

export const DesktopHeaderSelect = <T,>({
  options,
  onChange,
  value,
  icon,
  placeholder,
  defaultEmptyValue,
}: {
  options: { label: string; value: T }[];
  value?: string;
  onChange(value?: T): void;
  icon: React.ReactElement;
  placeholder: string;
  defaultEmptyValue?: T;
}) => {
  const [opened, setOpened] = useState(false);

  const ref = useClickOutside<HTMLDivElement>(() => setOpened(false));

  const selectedOption = options.find((o) => o.value === value);

  const handleClear = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onChange(defaultEmptyValue);
  };

  return (
    <div
      className={clsx(s.DesktopHeaderInput, s.DesktopHeaderSelect)}
      tabIndex={0}
      onClick={() => setOpened((o) => !o)}
      ref={ref}
    >
      {icon && <div className={s.icon}>{icon}</div>}
      {!!selectedOption && (
        <button className={s.reset} onClick={handleClear}>
          <BiX />
        </button>
      )}
      <div className={s.body}>
        <div className={s.text}>{selectedOption?.label ?? placeholder}</div>
      </div>
      {opened && (
        <section>
          <ul>
            {options?.map(({ label, value }) => (
              <li key={String(value)} onClick={() => onChange(value)}>
                {label}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
};

export const DesktopHeaderButton = ({
  icon,
  iconRight,
  onClick,
  children,
  disabled,
  buttonRef,
  expanded,
  active,
}: {
  icon?: React.ReactNode;
  iconRight?: React.ReactNode;
  onClick(): void;
  children: React.ReactNode;
  disabled?: boolean;
  /** For buttons that anchor a popover to themselves. */
  buttonRef?: React.Ref<HTMLButtonElement>;
  /** Set only by buttons that toggle a popover, which makes them aria-haspopup. */
  expanded?: boolean;
  /** Whether what the button toggles is currently on — accents the pill. */
  active?: boolean;
}) => {
  return (
    <ButtonPill
      ref={buttonRef}
      className={clsx(s.HeaderButton, active && s.HeaderButtonActive)}
      onClick={onClick}
      disabled={disabled}
      aria-haspopup={expanded === undefined ? undefined : 'dialog'}
      aria-expanded={expanded}
    >
      {icon}
      <span>{children}</span>
      {iconRight}
    </ButtonPill>
  );
};
